import { useParams } from 'react-router';
import { type ErrorLike } from '@apollo/client';
import { useState } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';

import { BackupDocument, IBackupQuery } from 'src/graphql/actions/backup/backup.query';

import { createRequiredContext } from '../createRequiredContext';

function assertDataExists(
  error: ErrorLike | undefined,
  data: IBackupQuery | undefined,
): asserts data is IBackupQuery & {
  backup: NonNullable<IBackupQuery['backup']>;
} {
  if (error) throw error;
  if (!data?.backup) throw new Error('Continuity escrow data not found');
}

const [useBackup, BackupProvider] = createRequiredContext<{
  backup: NonNullable<IBackupQuery['backup']>;
  me: { id: string };
  error?: ErrorLike;
  ui: IUiState;
  setActiveEnablingAssetId: (id: string | null) => void;
  setRemoveOpen: (open: boolean) => void;
  setTerminateOpen: (open: boolean) => void;
  setActivateConfirmation: (value: IActivateConfirmation) => void;
  refetch: () => Promise<unknown>;
}>();

type IActivateConfirmation = {
  isOpen: boolean;
  type: 'activate' | 'pending' | null;
};

type IUiState = {
  activeEnablingAssetId: string | null;
  isRemoveOpen: boolean;
  isTerminateOpen: boolean;
  activateConfirmation: IActivateConfirmation;
};

const BackupContextProvider = ({ children }: React.PropsWithChildren) => {
  const [ui, setUi] = useState<IUiState>({
    activeEnablingAssetId: null,
    isRemoveOpen: false,
    isTerminateOpen: false,
    activateConfirmation: { isOpen: false, type: null },
  });
  const { id } = useParams();
  const { data, error, refetch } = useSuspenseQuery(BackupDocument, {
    variables: {
      backupId: id as string,
    },
    skip: !id,
  });

  assertDataExists(error, data);

  const setActiveEnablingAssetId = (id: string | null) => setUi((ui) => ({ ...ui, activeEnablingAssetId: id }));

  const setRemoveOpen = (isRemoveOpen: boolean) => setUi((ui) => ({ ...ui, isRemoveOpen }));

  const setTerminateOpen = (isTerminateOpen: boolean) => setUi((ui) => ({ ...ui, isTerminateOpen }));

  const setActivateConfirmation = (activateConfirmation: IActivateConfirmation) =>
    setUi((ui) => ({ ...ui, activateConfirmation }));

  const providerValue = {
    backup: data.backup,
    me: { id: data.me.id ?? '' },
    error,
    ui,
    setActiveEnablingAssetId,
    setRemoveOpen,
    setTerminateOpen,
    setActivateConfirmation,
    refetch,
  };

  return <BackupProvider value={providerValue}>{children}</BackupProvider>;
};

export { useBackup, BackupContextProvider, BackupProvider };
